import { useEffect, useState } from "react";
import { api, formatError, formatNZD, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { nzDate } from "@/components/app/PlanCard";

const where = (b) => (b.everywhere ? "Everywhere" : b.placements.map((p) => (p === "HQ" ? "markdowns.nz" : p)).join(", "));

export default function BannerApprovals() {
  const [items, setItems] = useState([]);
  const load = async () => { try { const { data } = await api.get("/staff/banners"); setItems(data); } catch (e) { /* ignore */ } };
  useEffect(() => { load(); }, []);
  const act = async (b, action) => {
    let body = {};
    if (action === "reject") {
      const reason = window.prompt(`Reject ${b.business_name}'s banner? They'll get a full refund of ${formatNZD(b.amount)}.\n\nReason (sent to them, optional):`, "");
      if (reason === null) return;
      body = { reason };
    } else if (action === "remove" && !window.confirm("Take this banner down now? No refund is given.")) return;
    try { await api.post(`/staff/banners/${b.id}/${action}`, body); toast.success({ approve: "Banner is live", reject: "Rejected and refunded", remove: "Banner removed" }[action]); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const pending = items.filter((b) => b.status === "awaiting_approval").length;
  return (
    <Card data-testid="banner-approvals-card">
      <CardHeader>
        <CardTitle className="font-display text-xl">Banner adverts {pending > 0 && <Badge className="ml-2 bg-amber-500 hover:bg-amber-500">{pending} to approve</Badge>}</CardTitle>
        <CardDescription>Regional operators approve banners whose home region is theirs. The owner approves Everywhere (national) banners.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[520px] overflow-auto">
        {items.length === 0 && <div className="text-sm text-slate-500">No banner bookings yet.</div>}
        {items.map((b) => (
          <div key={b.id} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`banner-item-${b.id}`}>
            <img src={imageUrl(b.image_path)} alt="" className="w-full h-20 object-cover rounded-lg" />
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold">{b.business_name}</span>
              <Badge variant="outline">{b.status.replace("_", " ")}</Badge>
              <span className="text-xs text-slate-500">{b.period}ly · {formatNZD(b.amount)} · {where(b)}{b.ends_at && ` · ends ${nzDate(b.ends_at)}`}</span>
            </div>
            {b.message && <div className="text-xs text-slate-600">“{b.message}”</div>}
            <div className="text-xs text-slate-500">{b.contact_email}{b.link_url && <> · <a href={b.link_url} target="_blank" rel="noreferrer" className="underline">{b.link_url}</a></>}</div>
            {b.can_decide && (
              <div className="flex gap-2">
                {b.status === "awaiting_approval" && <>
                  <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => act(b, "approve")} data-testid={`banner-approve-${b.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => act(b, "reject")} data-testid={`banner-reject-${b.id}`}>Reject & refund</Button>
                </>}
                {b.status === "live" && <Button size="sm" variant="outline" onClick={() => act(b, "remove")} data-testid={`banner-remove-${b.id}`}>Take down</Button>}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
